import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SHAPES, SHAPE_MAP, defaultParams } from "../lib/sewingShapes";
import SewingShell from "../components/sewing/SewingShell";
import ShapeLab from "../components/sewing/ShapeLab";

/* ============================================================
   /sewing-primitives/lab — the shape lab as its own room.

   Lessons, cut lists and the creature generator deep-link in
   with ?shape=cylinder&radius=1.2..., so a teddy arm opens as
   the actual cylinder it is. Params are read once on mount.
   ============================================================ */

const SewingLab = () => {
  const [searchParams] = useSearchParams();
  const initial = useMemo(() => {
    const shapeId = searchParams.get("shape");
    if (!SHAPE_MAP[shapeId]) return null;
    const params = {};
    SHAPE_MAP[shapeId].params.forEach((p) => {
      const raw = searchParams.get(p.key);
      if (raw === null) return;
      if (p.type === "choice") {
        if (p.options.some((o) => o.value === raw)) params[p.key] = raw;
        return;
      }
      const num = Number(raw);
      if (Number.isFinite(num) && num > 0) {
        params[p.key] = Math.min(p.max, Math.max(p.min, num));
      }
    });
    return { shapeId, params };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeShapeId, setActiveShapeId] = useState(initial?.shapeId || "box");
  const [paramsByShape, setParamsByShape] = useState(() => {
    const base = Object.fromEntries(SHAPES.map((s) => [s.id, defaultParams(s)]));
    if (initial) {
      base[initial.shapeId] = { ...base[initial.shapeId], ...initial.params };
    }
    return base;
  });

  return (
    <SewingShell
      active="lab"
      title="The lab"
      intro={
        <p className="mt-4 max-w-2xl text-muted-foreground">
          Every primitive, shown both ways at once: the flat pattern and the
          3D shape it folds into. Sliders change the shape; the pattern math
          updates live, because the pattern <em>is</em> the math.
        </p>
      }
    >
      <div className="mt-10">
        <ShapeLab
          activeShapeId={activeShapeId}
          setActiveShapeId={setActiveShapeId}
          paramsByShape={paramsByShape}
          setParamsByShape={setParamsByShape}
        />
      </div>

      <p className="mt-12 text-center text-sm text-muted-foreground">
        Wondering what to do with these?{" "}
        <Link
          to="/sewing-primitives/learn"
          className="inline-flex items-center gap-0.5 font-medium text-primary hover:underline"
        >
          The curriculum walks them in order
          <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>{" "}
        — or skip ahead and{" "}
        <Link
          to="/sewing-primitives/make"
          className="inline-flex items-center gap-0.5 font-medium text-primary hover:underline"
        >
          make something
          <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
        . 🧵
      </p>
    </SewingShell>
  );
};

export default SewingLab;
